import React from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';

import { STATUS_FILTER_VALUES } from 'components/logistics/fulfillmentLifecycle/constants';
import { Badge } from 'shadcn/components/ui/Badge';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { cn } from 'shadcn/lib/utils';

/**
 * RiskSummaryBadge
 *
 * Clickable summary badge that toggles the dashboard status filter.
 */
function RiskSummaryBadge({
    baseClassName,
    count,
    countClassName,
    focusClassName,
    hoverClassName,
    icon: Icon,
    iconClassName,
    label,
    labelClassName,
    onStatusFilterClick,
    selectedClassName,
    status,
    statusFilter,
}) {
    const isSelected = statusFilter === status;

    return (
        <button
            type="button"
            onClick={() => onStatusFilterClick(status)}
            aria-pressed={isSelected}
            className={cn(
                'inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                baseClassName,
                hoverClassName,
                focusClassName,
                isSelected && selectedClassName,
            )}
        >
            <Icon className={cn('h-5 w-5', iconClassName)} aria-hidden="true" />
            <span className={cn('text-2xl font-bold', countClassName)}>{count}</span>
            <span className={cn('text-sm font-medium', labelClassName)}>{label}</span>
        </button>
    );
}

/**
 * SummaryAlerts
 *
 * Displays risk-level summary counts for the dashboard.
 * Shows critical, at-risk, and on-track badges alongside
 * total route/shipment/throughput stats.
 *
 * @param {Object} props
 * @param {Object} props.summary - Summary data from the API
 * @param {number} props.summary.critical - Count of critical routes
 * @param {number} props.summary.atRisk - Count of at-risk routes
 * @param {number} props.summary.onTrack - Count of on-track routes
 * @param {number} props.summary.totalRoutes - Total route count
 * @param {number} props.summary.totalShipments - Total shipment count
 * @param {number} props.summary.facilityThroughput - Facility throughput per hour
 * @param {string} props.statusFilter - Current active status filter
 * @param {Function} props.onStatusFilterClick - Called when a risk badge is clicked
 */
function SummaryAlerts({ onStatusFilterClick, statusFilter, summary }) {
    const {
        atRisk = 0,
        critical = 0,
        facilityThroughput = 0,
        onTrack = 0,
        totalRoutes = 0,
        totalShipments = 0,
    } = summary || {};

    return (
        <div className="space-y-3">
            {/* Risk Summary Badges */}
            <div className="flex gap-3 flex-wrap">
                <RiskSummaryBadge
                    status={STATUS_FILTER_VALUES.CRITICAL}
                    count={critical}
                    label="Critical Issues"
                    icon={XCircle}
                    statusFilter={statusFilter}
                    onStatusFilterClick={onStatusFilterClick}
                    baseClassName="bg-red-50 border-red-200"
                    selectedClassName="ring-2 ring-red-400 bg-red-100 border-red-300"
                    hoverClassName="hover:bg-red-200"
                    focusClassName="focus-visible:ring-red-400"
                    iconClassName="text-red-500"
                    countClassName="text-red-600"
                    labelClassName="text-red-700"
                />

                <RiskSummaryBadge
                    status={STATUS_FILTER_VALUES.AT_RISK}
                    count={atRisk}
                    label="At Risk"
                    icon={AlertTriangle}
                    statusFilter={statusFilter}
                    onStatusFilterClick={onStatusFilterClick}
                    baseClassName="bg-amber-50 border-amber-200"
                    selectedClassName="ring-2 ring-amber-400 bg-amber-100 border-amber-300"
                    hoverClassName="hover:bg-amber-200"
                    focusClassName="focus-visible:ring-amber-400"
                    iconClassName="text-amber-500"
                    countClassName="text-amber-600"
                    labelClassName="text-amber-700"
                />

                <RiskSummaryBadge
                    status={STATUS_FILTER_VALUES.ON_TRACK}
                    count={onTrack}
                    label="On Track"
                    icon={CheckCircle2}
                    statusFilter={statusFilter}
                    onStatusFilterClick={onStatusFilterClick}
                    baseClassName="bg-green-50 border-green-200"
                    selectedClassName="ring-2 ring-green-400 bg-green-100 border-green-300"
                    hoverClassName="hover:bg-green-200"
                    focusClassName="focus-visible:ring-green-400"
                    iconClassName="text-green-500"
                    countClassName="text-green-600"
                    labelClassName="text-green-700"
                />
            </div>

            {/* Stats Summary */}
            <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    {totalRoutes} routes
                </Badge>
                <Badge variant="outline" className="text-xs font-normal text-muted-foreground">
                    {totalShipments} shipments
                </Badge>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex">
                            <Badge variant="outline" className="text-xs font-normal text-muted-foreground gap-1">
                                {facilityThroughput}/hr throughput
                                <Info className="h-3 w-3" />
                            </Badge>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        Based on the current 2-hour packing rate. Does not reflect historical throughput.
                    </TooltipContent>
                </Tooltip>
            </div>
        </div>
    );
}

export default SummaryAlerts;
